import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { useLiveState, yellowBtn, TYPES, brandPalette, textOn } from "@/lib/airep";
import { Results, AirepLogo } from "@/components/airep/Results";

const pid = () => {
  let p = localStorage.getItem("airep_pid");
  if (!p) { p = (crypto.randomUUID ? crypto.randomUUID() : String(Math.random()).slice(2) + Date.now()); localStorage.setItem("airep_pid", p); }
  return p;
};

function CodeEntry() {
  const [code, setCode] = useState("");
  const navigate = useNavigate();
  return (
    <form onSubmit={(e) => { e.preventDefault(); if (code.length === 6) navigate(`/r/${code}`); }} className="w-full max-w-sm space-y-4" data-testid="join-code-form">
      <h1 className="font-display text-3xl font-extrabold text-center">Inserisci il codice</h1>
      <input inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} placeholder="123456"
        className="w-full text-center font-mono text-4xl tracking-[0.3em] bg-white border-2 border-slate-200 focus:border-slate-900 outline-none rounded-2xl py-4" data-testid="join-code-input" />
      <button disabled={code.length !== 6} className={`${yellowBtn} w-full py-4 text-lg`} data-testid="join-code-submit">Entra <ArrowRight className="h-5 w-5" /></button>
    </form>
  );
}

function VoteForm({ slide, code, onDone, accent }) {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async (value) => {
    setBusy(true); setErr("");
    try { await api.post(`/airep/join/${code}/vote`, { pid: pid(), slide_id: slide.id, value }); setText(""); onDone(); }
    catch (e) { const d = e?.response?.data?.detail; if (d === "Hai già risposto a questa domanda") onDone(); else setErr(d || "Invio non riuscito"); }
    setBusy(false);
  };
  return (
    <div className="space-y-3" data-testid="vote-form">
      {slide.type === "poll" && slide.options.map((o, i) => (
        <button key={i} disabled={busy} onClick={() => send(i)} className="w-full text-left bg-white border-2 border-slate-200 hover:border-slate-900 rounded-2xl px-5 py-4 font-medium text-base transition-colors" data-testid={`vote-option-${i}`}>{o}</button>
      ))}
      {slide.type === "scale" && (
        <div className="grid grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5].map((n) => <button key={n} disabled={busy} onClick={() => send(n)} className="aspect-square bg-white border-2 border-slate-200 hover:border-slate-900 hover:bg-[#E3FF00] rounded-2xl font-display text-2xl font-bold transition-colors" data-testid={`vote-scale-${n}`}>{n}</button>)}
        </div>
      )}
      {(slide.type === "wordcloud" || slide.type === "open") && (
        <form onSubmit={(e) => { e.preventDefault(); if (text.trim()) send(text); }} className="space-y-3">
          {slide.type === "open"
            ? <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={280} rows={4} placeholder="Scrivi la tua risposta…" className="w-full bg-white border-2 border-slate-200 focus:border-slate-900 outline-none rounded-2xl p-4 text-base" data-testid="vote-text-input" />
            : <input value={text} onChange={(e) => setText(e.target.value)} maxLength={30} placeholder="Una parola…" className="w-full bg-white border-2 border-slate-200 focus:border-slate-900 outline-none rounded-2xl px-4 py-4 text-lg" data-testid="vote-text-input" />}
          <button disabled={busy || !text.trim()} className={`${yellowBtn} w-full py-4 text-lg`} style={accent ? { background: accent, color: textOn(accent), borderColor: accent } : undefined} data-testid="vote-submit">Invia</button>
        </form>
      )}
      {err && <p className="text-sm text-rose-600 text-center" data-testid="vote-error">{err}</p>}
    </div>
  );
}

export default function AiReportJoin() {
  const { code } = useParams();
  const { state, error } = useLiveState(code, `/airep/join/${code}`);
  const [voted, setVoted] = useState(() => JSON.parse(localStorage.getItem("airep_voted") || "{}"));
  const key = state?.slide ? `${state.session_id}:${state.slide.id}` : null;
  const markVoted = () => { const v = { ...voted, [key]: (voted[key] || 0) + 1 }; setVoted(v); localStorage.setItem("airep_voted", JSON.stringify(v)); };
  const done = key && voted[key];
  useEffect(() => { document.title = "AI-Report · Partecipa"; }, []);
  useEffect(() => {
    if (!code) return undefined;
    const beat = () => api.post(`/airep/join/${code}/heartbeat`, { pid: pid() }).catch(() => {});
    beat();
    const t = setInterval(beat, 15000);
    return () => clearInterval(t);
  }, [code]);
  const accent = state?.brand?.colors?.primary;
  const palette = brandPalette(state?.brand);
  return (
    <div className="min-h-screen bg-[#fafaf9] text-slate-900 flex flex-col" data-testid="airep-join-page">
      <header className="px-4 h-14 flex items-center justify-between border-b border-slate-200 bg-white" style={accent ? { borderBottom: `3px solid ${accent}` } : undefined}>
        {state?.brand?.logo_url ? <img src={state.brand.logo_url} alt={state.brand.name} className="h-8 max-w-[140px] object-contain" data-testid="join-brand-logo" /> : <AirepLogo className="h-7" />}
        {state && <span className="text-xs text-slate-500 flex items-center gap-1.5"><span className="h-2 w-2 rounded-full bg-[#84cc16] animate-pulse" />{state.present} presenti</span>}
      </header>
      <main className="flex-1 flex flex-col items-center justify-start px-4 py-8 w-full max-w-lg mx-auto">
        {!code && <CodeEntry />}
        {code && error && !state && <div className="text-center space-y-4" data-testid="join-error"><p className="text-lg">{error}</p><CodeEntry /></div>}
        {state && state.status === "waiting" && <div className="text-center py-16 space-y-3" data-testid="join-waiting"><div className="h-3 w-3 rounded-full bg-[#84cc16] animate-pulse mx-auto" /><h1 className="font-display text-3xl font-extrabold">Il sondaggio sta per iniziare</h1><p className="text-slate-500">{state.title}<br />Resta su questa pagina: si aprirà da sola.</p></div>}
        {state && state.status === "ended" && <div className="text-center py-16" data-testid="join-ended"><h1 className="font-display text-3xl font-extrabold">Sessione terminata</h1><p className="text-slate-500 mt-2">Grazie per aver partecipato!</p></div>}
        {state && state.status === "live" && state.slide && (
          <div className="w-full space-y-6 animate-in-up" key={state.slide.id}>
            <div>
              <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{TYPES[state.slide.type]} · {state.active_slide + 1}/{state.slides_count}</span>
              <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-2" data-testid="join-question">{state.slide.question}</h1>
            </div>
            {state.locked ? <p className="text-center text-slate-500 py-6" data-testid="join-locked">Votazioni chiuse per questa domanda.</p>
              : done ? <div className="text-center py-4" data-testid="join-thanks"><div className="h-14 w-14 rounded-full bg-[#E3FF00] grid place-items-center mx-auto"><Check className="h-7 w-7" /></div><p className="font-display font-bold text-xl mt-3">Risposta inviata!</p></div>
                : <VoteForm slide={state.slide} code={code} onDone={markVoted} accent={accent} />}
            {state.results && (done || state.locked) && <div className="pt-4 border-t border-slate-200"><Results slide={state.slide} results={state.results} palette={palette} /></div>}
          </div>
        )}
      </main>
      <footer className="text-center text-[11px] text-slate-400 pb-4">AI-Report · powered by mypasspartu</footer>
    </div>
  );
}
